import type { MachineryCategories } from '~/types'
import type { MachineryUVVReportItemType } from '~/prisma/enums'

export type MachineryUVVReportFields = 'machineryCondition' | 'isReadyForOperation' | 'inspectionType'
type MachineryUVVReportItemHeader = 'LiftingMechanism'
  | 'Forks'
  | 'ChainsRope'
  | 'DriveSystemAndBrakes'
  | 'DriversSeatAndControls'
  | 'ElectricalEquipment'
  | 'HydraulicSystem'
  | 'FrameSafetyEquipment'
  | 'VariousSpecialEquipment'
  | 'Documents'
  | 'Checklist'
  | 'RemoteControl'
  | 'Cabin'
  | 'Wheels'
  | 'Safety'
  | 'Accessories'
  | 'LoadDiagram'
  | 'Miscellaneous'
  | 'GeneralInformation'
  | 'BasicMachineAndChassis'
  | 'CombustionEngine'
  | 'CabinAndOperatorControls'
  | 'BoomAndBasket'
  | 'LoadTest'

type ReportConfigItemField = 'isSecurityCheckInOrder'
  | 'isMeasurementInOrder'
  | 'isComponentCompleted'
  | 'remark'
  | 'rectifiedDefects'
  | 'value'
  | 'liftingWeight'
  | 'tons'

export interface MachineryUVVReportConfigItem {
  type: MachineryUVVReportItemType
  fields: Partial<Record<ReportConfigItemField, { isRequired: boolean }>>
}
export type MachineryCategoryMachineryUVVReportItemConfig = Partial<Record<MachineryUVVReportItemHeader, MachineryUVVReportConfigItem[]>>
interface MachineryCategoryMachineryUVVReportConfig {
  items: MachineryCategoryMachineryUVVReportItemConfig
  reportFields: Set<MachineryUVVReportFields>
}

const forkliftBaseItemFields = {
  isSecurityCheckInOrder: { isRequired: true },
  isMeasurementInOrder: { isRequired: true },
  remark: { isRequired: false },
  rectifiedDefects: { isRequired: false },
}
const forkliftItemConfig: MachineryCategoryMachineryUVVReportItemConfig = {
  LiftingMechanism: [
    { type: 'LiftingMechanismGeneral', fields: forkliftBaseItemFields },
  ],
  Forks: [
    { type: 'GeneralForks', fields: forkliftBaseItemFields },
    { type: 'ThicknessAtForkBend', fields: forkliftBaseItemFields },
    { type: 'PermanentDeformation', fields: forkliftBaseItemFields },
    { type: 'CracksAtBendAndSuspension', fields: forkliftBaseItemFields },
  ],
  ChainsRope: [
    { type: 'LengthOverAtLeast6Divisions', fields: forkliftBaseItemFields },
  ],
  DriveSystemAndBrakes: [
    { type: 'ExhaustInspectionForDieselEngines', fields: forkliftBaseItemFields },
    { type: 'ServiceBrakeBrakeLine', fields: forkliftBaseItemFields },
    { type: 'ParkingBrakeBrakingPerformance', fields: forkliftBaseItemFields },
    { type: 'BrakeOnDrawbar', fields: forkliftBaseItemFields },
    { type: 'BrakeSystem', fields: forkliftBaseItemFields },
    { type: 'WheelsAndTires', fields: forkliftBaseItemFields },
  ],
  DriversSeatAndControls: [
    { type: 'DriverRestraintSystem', fields: forkliftBaseItemFields },
    { type: 'SeatAttachment', fields: forkliftBaseItemFields },
    { type: 'SteeringSystem', fields: forkliftBaseItemFields },
    { type: 'ControlsAndLabeling', fields: forkliftBaseItemFields },
  ],
  ElectricalEquipment: [
    { type: 'BatteryCondition', fields: forkliftBaseItemFields },
    { type: 'BatteryMounting', fields: forkliftBaseItemFields },
    { type: 'BatteryData', fields: forkliftBaseItemFields },
    { type: 'SeatSwitchOrShutOffDevice', fields: forkliftBaseItemFields },
    { type: 'EmergencyStopSwitch', fields: forkliftBaseItemFields },
    { type: 'SafetyDisconnectSwitch', fields: forkliftBaseItemFields },
    { type: 'ElectricalWiring', fields: forkliftBaseItemFields },
    { type: 'SafetySwitchOnDrawbar', fields: forkliftBaseItemFields },
  ],
  HydraulicSystem: [
    { type: 'LiftSystemLoweringTest', fields: forkliftBaseItemFields },
    { type: 'TiltSystemCreepTest', fields: forkliftBaseItemFields },
    { type: 'LeaksAndDamages', fields: forkliftBaseItemFields },
  ],
  FrameSafetyEquipment: [
    { type: 'AttachmentPoints', fields: forkliftBaseItemFields },
    { type: 'FrameAndSafetyEquipment', fields: forkliftBaseItemFields },
    { type: 'TrailerCoupling', fields: forkliftBaseItemFields },
    { type: 'FloorOpeningsForLpgForklifts', fields: forkliftBaseItemFields },
    { type: 'HoodLocking', fields: forkliftBaseItemFields },
  ],
  VariousSpecialEquipment: [
    { type: 'Signage', fields: forkliftBaseItemFields },
    { type: 'OperatingManual', fields: forkliftBaseItemFields },
    { type: 'Attachments', fields: forkliftBaseItemFields },
    { type: 'AdditionalEquipment', fields: forkliftBaseItemFields },
  ],
}

const craneBaseItemFields = {
  isSecurityCheckInOrder: { isRequired: true },
  value: { isRequired: false },
  remark: { isRequired: false },
  rectifiedDefects: { isRequired: false },
}
const craneAccessoryItemFields = {
  ...craneBaseItemFields,
  liftingWeight: { isRequired: true },
}
const craneItemConfig: MachineryCategoryMachineryUVVReportItemConfig = {
  Documents: [
    { type: 'InspectionBook', fields: craneBaseItemFields },
    { type: 'OperatingManual', fields: craneBaseItemFields },
    { type: 'LastProtocol', fields: craneBaseItemFields },
    { type: 'TypePlateOrCEMarking', fields: craneBaseItemFields },
    { type: 'WarningOrInformationSign', fields: craneBaseItemFields },
    { type: 'ElectricalTestDGUVV3', fields: craneBaseItemFields },
  ],
  Checklist: [
    { type: 'BatteryCondition', fields: craneBaseItemFields },
    { type: 'ElectricalBox', fields: craneBaseItemFields },
    { type: 'HydraulicFluidLevel', fields: craneBaseItemFields },
    { type: 'HydraulicHosesCondition', fields: craneBaseItemFields },
    { type: 'HydraulicHosesAge', fields: craneBaseItemFields },
    { type: 'HydraulicSystemTightness', fields: craneBaseItemFields },

  ],
  RemoteControl: [
    { type: 'RemoteControlCondition', fields: craneBaseItemFields },
    { type: 'RemoteControlLabeling', fields: craneBaseItemFields },
    { type: 'RemoteControlFunction', fields: craneBaseItemFields },
  ],
  Cabin: [
    { type: 'CabinCondition', fields: craneBaseItemFields },
    { type: 'CabinSeat', fields: craneBaseItemFields },
    { type: 'CabinControl', fields: craneBaseItemFields },
  ],
  Wheels: [
    { type: 'WheelsCondition', fields: craneBaseItemFields },
    { type: 'WheelsConfirmation', fields: craneBaseItemFields },
  ],
  Safety: [
    { type: 'EmergencyStopSwitch', fields: craneBaseItemFields },
    { type: 'EndSwitch', fields: craneBaseItemFields },
    { type: 'LoadAssessment', fields: craneBaseItemFields },
    { type: 'DamageToSheetMetalEtc', fields: craneBaseItemFields },
    { type: 'BrakeFunction', fields: craneBaseItemFields },
  ],
  Accessories: [
    { type: 'AccessoryMainHook', fields: craneAccessoryItemFields },
    { type: 'AccessoryMechanicalJIB', fields: craneAccessoryItemFields },
    { type: 'AccessoryHydraulicJIB', fields: craneAccessoryItemFields },
    { type: 'AccessoryRopeWinch', fields: craneAccessoryItemFields },
    { type: 'AccessoryFork', fields: craneAccessoryItemFields },
  ],
  LoadDiagram: [
    { type: 'RadiusControlValueMin', fields: craneBaseItemFields },
    { type: 'RadiusControlValueMax', fields: craneBaseItemFields },
    { type: 'OverloadWithR', fields: { ...craneBaseItemFields, tons: { isRequired: true } } },
  ],
  Miscellaneous: [
    { type: 'Miscellaneous', fields: craneBaseItemFields },
  ],
}

const platformBaseItemFields = {
  isSecurityCheckInOrder: { isRequired: true },
  isComponentCompleted: { isRequired: true },
  remark: { isRequired: false },
  rectifiedDefects: { isRequired: false },
}

const platformItemConfig: MachineryCategoryMachineryUVVReportItemConfig = {
  GeneralInformation: [
    { type: 'InspectionBook', fields: platformBaseItemFields },
    { type: 'OperatingManual', fields: platformBaseItemFields },
    { type: 'RiskAssessment', fields: platformBaseItemFields },
    { type: 'LastProtocol', fields: platformBaseItemFields },
    { type: 'TypePlateOrCEMarking', fields: platformBaseItemFields },
    { type: 'DeclarationOfConformity', fields: platformBaseItemFields },
    { type: 'WarningOrInformationSign', fields: platformBaseItemFields },
    { type: 'LicensePlate', fields: platformBaseItemFields },
    { type: 'ABEVehicleRegistrationDocument', fields: platformBaseItemFields },
    { type: 'FirstAidKitOrWarningTriangle', fields: platformBaseItemFields },
    { type: 'WarningLight', fields: platformBaseItemFields },
    { type: 'Tools', fields: platformBaseItemFields },
  ],
  BasicMachineAndChassis: [
    { type: 'FrameBoltBearing', fields: platformBaseItemFields },
    { type: 'FairingMudguard', fields: platformBaseItemFields },
    { type: 'StepHandleAscent', fields: platformBaseItemFields },
    { type: 'Rotator', fields: platformBaseItemFields },
    { type: 'TransportLock', fields: platformBaseItemFields },
    { type: 'Counterweight', fields: platformBaseItemFields },
    { type: 'TowingEquipment', fields: platformBaseItemFields },
    { type: 'LashingLiftingPoint', fields: platformBaseItemFields },
    { type: 'WheelChock', fields: platformBaseItemFields },
    { type: 'WheelTireRims', fields: platformBaseItemFields },
    { type: 'Gearbox', fields: platformBaseItemFields },
    { type: 'AxlesAndSuspension', fields: platformBaseItemFields },
    { type: 'SteeringKnuckleAndTieRods', fields: platformBaseItemFields },
    { type: 'CardanShafts', fields: platformBaseItemFields },
    { type: 'TractionMotorAndTractionDrive', fields: platformBaseItemFields },
    { type: 'MachineSupport', fields: platformBaseItemFields },
    { type: 'MachineSupportPlate', fields: platformBaseItemFields },
    { type: 'MachineSupportBasePlate', fields: platformBaseItemFields },
  ],
  DriveSystemAndBrakes: [
    { type: 'BrakeSystem', fields: platformBaseItemFields },
    { type: 'BrakeLine', fields: platformBaseItemFields },
    { type: 'BrakePedal', fields: platformBaseItemFields },
    { type: 'BrakingEffect', fields: platformBaseItemFields },
    { type: 'ParkingBrakeBrakingPerformance', fields: platformBaseItemFields },
    { type: 'BrakeCableLinkages', fields: platformBaseItemFields },
    { type: 'BrakeBooster', fields: platformBaseItemFields },
  ],
  HydraulicSystem: [
    { type: 'OilTankFilter', fields: platformBaseItemFields },
    { type: 'HydraulicConnections', fields: platformBaseItemFields },
    { type: 'HydraulicHosesCondition', fields: platformBaseItemFields },
    { type: 'HydraulicControlBlocks', fields: platformBaseItemFields },
    { type: 'HydraulicPump', fields: platformBaseItemFields },
    { type: 'HydraulicCylinders', fields: platformBaseItemFields },
    { type: 'PressureReliefValves', fields: platformBaseItemFields },
  ],
  ElectricalEquipment: [
    { type: 'StarterAndLighting', fields: platformBaseItemFields },
    { type: 'BatteryAndFuse', fields: platformBaseItemFields },
    { type: 'CableAndConnection', fields: platformBaseItemFields },
    { type: 'SwitchAndDistributionBox', fields: platformBaseItemFields },
    { type: 'IndicatorLight', fields: platformBaseItemFields },
    { type: 'IgnitionLockAndKeySwitch', fields: platformBaseItemFields },
    { type: 'EmergencyStopSwitch', fields: platformBaseItemFields },
    { type: 'TiltSensor', fields: platformBaseItemFields },
    { type: 'WarningDevices', fields: platformBaseItemFields },
    { type: 'EmergencyDrain', fields: platformBaseItemFields },
    { type: 'LMBOverloadDevice', fields: platformBaseItemFields },
  ],
  CombustionEngine: [
    { type: 'EngineMounting', fields: platformBaseItemFields },
    { type: 'EngineStartStop', fields: platformBaseItemFields },
    { type: 'EngineStarterInterlock', fields: platformBaseItemFields },
    { type: 'SpeedControl', fields: platformBaseItemFields },
    { type: 'VBelt', fields: platformBaseItemFields },
    { type: 'OilFuelLevel', fields: platformBaseItemFields },
    { type: 'OilCoolantTank', fields: platformBaseItemFields },
    { type: 'DriveAuxiliaryDrive', fields: platformBaseItemFields },
    { type: 'ExhaustSystem', fields: platformBaseItemFields },
    { type: 'SoundInsulation', fields: platformBaseItemFields },
    { type: 'EngineConnections', fields: platformBaseItemFields },
    { type: 'EngineFuelTank', fields: platformBaseItemFields },
  ],
  CabinAndOperatorControls: [
    { type: 'AccessAndDoor', fields: platformBaseItemFields },
    { type: 'WindowAndPaneAndMirror', fields: platformBaseItemFields },
    { type: 'WiperAndWasher', fields: platformBaseItemFields },
    { type: 'DriverSeatAndRestraintSystem', fields: platformBaseItemFields },
    { type: 'HeatingAndVentilation', fields: platformBaseItemFields },
    { type: 'CoverAndEmergencyExit', fields: platformBaseItemFields },
    { type: 'ControlElement', fields: platformBaseItemFields },
    { type: 'PedalAndJoyStickAndLabel', fields: platformBaseItemFields },
    { type: 'ProtectiveGrille', fields: platformBaseItemFields },
    { type: 'AllCabinFunctions', fields: platformBaseItemFields },
  ],
  BoomAndBasket: [
    { type: 'BoomSection', fields: platformBaseItemFields },
    { type: 'TelescopicExtension', fields: platformBaseItemFields },
    { type: 'BoomChainAndCableAndAntiFallProtection', fields: platformBaseItemFields },
    { type: 'BoomPulley', fields: platformBaseItemFields },
    { type: 'BoomBearingAndBolt', fields: platformBaseItemFields },
    { type: 'BoomLock', fields: platformBaseItemFields },
    { type: 'WorkPlatformOrBasket', fields: platformBaseItemFields },
    { type: 'BoomShearGuardAndArm', fields: platformBaseItemFields },
  ],
  LoadTest: [
    { type: 'LoadTesting', fields: platformBaseItemFields },
  ],
}

export const machineryUVVReportConfigByMachineryCategories: Readonly<Partial<Record<MachineryCategories, MachineryCategoryMachineryUVVReportConfig>>> = {
  forklift: {
    items: forkliftItemConfig,
    reportFields: new Set([]),
  },
  crane: {
    items: craneItemConfig,
    reportFields: new Set(['machineryCondition', 'isReadyForOperation']),
  },
  platform: {
    items: platformItemConfig,
    reportFields: new Set(['machineryCondition', 'isReadyForOperation', 'inspectionType']),
  },
}

// Used in pdf generation to create inspection items table
export const machineryUVVReportBaseItemFields: Readonly<Partial<Record<MachineryCategories, ReportConfigItemField[]>>> = {
  forklift: Object.keys(forkliftBaseItemFields) as ReportConfigItemField[],
  crane: Object.keys(craneBaseItemFields) as ReportConfigItemField[],
  platform: Object.keys(platformBaseItemFields) as ReportConfigItemField[],
}
